import React, { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Box, Modal, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { Link } from "react-router-dom";
import Cookies from 'js-cookie';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiEdit, FiMap, FiPackage, FiCreditCard, FiEye, FiTruck, 
  FiCalendar, FiDollarSign, FiSearch, FiX, FiSave, FiLink 
} from 'react-icons/fi';

import Amex from "../../../assets/img/amex.svg";
import Mastercard from "../../../assets/img/mastercard.svg";
import Visa from "../../../assets/img/visa.svg";

const brandLogos = {
  amex: Amex,
  mastercard: Mastercard,
  visa: Visa
};

export default function Orders() {
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [saveLoading, setSaveLoading] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState('');

  useEffect(() => {
    async function fetchData() {
      try {
        const adminToken = Cookies.get('adminKey');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URI}admin/orders`, {
          method: 'GET',
          headers: {
            "Admin-Token": adminToken
          }
        });

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        const data = await response.json();

        // Map the backend data to match the DataGrid's required row format
        const formattedRows = data.map((order) => ({
          id: order.id,
          status: order.status,
          paymentMethod: order.paymentMethodData,
          paymentMethodId: order.paymentMethodId,
          stripePaymentId: order.stripePaymentId,
          shippingAddress: order.shippingAddress,
          adminViewed: order.adminViewed ? 'Yes' : 'No',
          adminShipped: order.adminShipped ? 'Yes' : 'No',
          orderCreationTime: new Date(order.orderCreationTime).toLocaleString('it-IT', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          }),
          rawOrderCreationTime: order.orderCreationTime,
          paymentExecutionTime: new Date(order.paymentExecutionTime).toLocaleString('it-IT', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          }),
          rawPaymentExecutionTime: order.paymentExecutionTime,
          paymentTotal: order.paymentTotal,
          rawData: order
        }));

        setRows(formattedRows);
        setFilteredRows(formattedRows);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  // Filter orders based on search term and status
  useEffect(() => {
    let result = [...rows];
    
    // Filter by search term
    if (searchTerm) {
      const lowercasedTerm = searchTerm.toLowerCase();
      result = result.filter(order => 
        order.shippingAddress?.toLowerCase().includes(lowercasedTerm) ||
        order.stripePaymentId?.toLowerCase().includes(lowercasedTerm) ||
        order.id.toString().includes(lowercasedTerm)
      );
    }
    
    // Filter by status
    if (filterStatus !== 'all') {
      result = result.filter(order => order.status === filterStatus);
    }
    
    setFilteredRows(result);
  }, [searchTerm, filterStatus, rows]);

  const handleEditClick = (orderId) => {
    const orderToEdit = rows.find(order => order.id === orderId);
    setSelectedOrder(orderToEdit);
  };

  const handleCloseModal = () => {
    setSelectedOrder(null);
  };

  const handleSaveOrder = async () => {
    setSaveLoading(true);
    try {
      const adminToken = Cookies.get('adminKey');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URI}admin/updateOrder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Admin-Token': adminToken
        },
        body: JSON.stringify({
          orderId: selectedOrder.id,
          status: selectedOrder.status,
          adminShipped: selectedOrder.adminShipped === 'Yes',
          adminViewed: selectedOrder.adminViewed === 'Yes',
          shippingAddress: selectedOrder.shippingAddress
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update order');
      }

      // Update local state with new values
      const updatedRows = rows.map(order => {
        if (order.id === selectedOrder.id) {
          return {
            ...order,
            status: selectedOrder.status,
            adminShipped: selectedOrder.adminShipped,
            adminViewed: selectedOrder.adminViewed,
            shippingAddress: selectedOrder.shippingAddress
          };
        }
        return order;
      });
      
      setRows(updatedRows);
      setSaveLoading(false);
      handleCloseModal();
    } catch (error) {
      console.error('Error saving order:', error);
      setSaveLoading(false);
    }
  };

  const handleCopyAddress = (address) => {
    navigator.clipboard.writeText(address);
    setCopyFeedback('Address copied to clipboard!');
    
    setTimeout(() => {
      setCopyFeedback('');
    }, 2000);
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      'pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'paid': 'bg-green-100 text-green-800 border-green-200',
      'processing': 'bg-blue-100 text-blue-800 border-blue-200',
      'shipped': 'bg-purple-100 text-purple-800 border-purple-200',
      'delivered': 'bg-teal-100 text-teal-800 border-teal-200',
      'cancelled': 'bg-red-100 text-red-800 border-red-200',
      'default': 'bg-gray-100 text-gray-800 border-gray-200'
    };

    const statusIcons = {
      'pending': <FiCreditCard className="mr-1" />,
      'paid': <FiDollarSign className="mr-1" />,
      'processing': <FiPackage className="mr-1" />,
      'shipped': <FiTruck className="mr-1" />,
      'delivered': <FiPackage className="mr-1" />,
      'cancelled': <FiX className="mr-1" />,
    };
    
    return (
      <div className={`px-3 py-1 rounded-full text-xs font-medium flex items-center w-fit ${statusStyles[status] || statusStyles.default} border`}>
        {statusIcons[status]}
        <span className="capitalize">{status}</span>
      </div>
    );
  };

  const columns = [
    { 
      field: 'id', 
      headerName: 'ID', 
      width: 60,
      renderCell: (params) => (
        <Link className="font-mono text-xs bg-gray-100 px-2 py-1 rounded-lg text-gray-700" to={`/admin/orders/${params.value}`}>
          #{params.value}
        </Link>
      )
    },
    { 
      field: 'status', 
      headerName: 'Stato', 
      width: 130,
      renderCell: (params) => {
        return(
          <div className="mt-3">
            {getStatusBadge(params.value)}
          </div>
        )
      }
    },
    {
      field: 'paymentMethod',
      headerName: 'Metodo di Pagamento',
      width: 180,
      renderCell: (params) => {
        try {
          const paymentMethodData = params.row.paymentMethod ? JSON.parse(params.row.paymentMethod) : null;
          const cardBrand = paymentMethodData?.brand;
          const last4 = paymentMethodData?.last4;
    
          if (!cardBrand || !last4) return <span className="text-gray-400 italic">No card info</span>;
          
          const brandLogo = brandLogos[cardBrand] || brandLogos.visa
    
          return (
            <div className="flex items-center">
              <div className="bg-white p-1 rounded border border-gray-200 mr-4">
                <img src={brandLogo} alt={cardBrand} width={24} height={24} />
              </div>
              <span className="font-medium">•••• {last4}</span>
            </div>
          );
        } catch (error) {
          return <span className="text-gray-400 italic">No card info</span>;
        }
      }
    },
    { 
      field: 'stripePaymentId', 
      headerName: 'ID Pagamento', 
      width: 220,
      renderCell: (params) => (
        <div className="flex items-center mt-4">
          <a 
            href={`https://dashboard.stripe.com/payments/${params.row.stripePaymentId}`} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="flex items-center text-blue-500 hover:text-blue-700 hover:underline"
          >
            <FiLink className="mr-1" />
            <span className="font-mono text-xs">{params.row.stripePaymentId}</span>
          </a>
        </div>
      )
    },
    { 
      field: 'shippingAddress', 
      headerName: 'Indirizzo Spedizione', 
      width: 300,
      renderCell: (params) => (
        <motion.div 
          className="flex items-center cursor-pointer group"
          onClick={() => handleCopyAddress(params.value)}
          whileHover={{ backgroundColor: "#f3f4f6" }}
          transition={{ duration: 0.2 }}
        >
          <span className="truncate">{params.value}</span>
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }}
            className="ml-2 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            (Click to copy)
          </motion.div>
        </motion.div>
      )
    },
    { 
      field: 'adminViewed', 
      headerName: 'Visto', 
      width: 80,
      renderCell: (params) => (
        <div className={`flex items-center ${params.value === 'Yes' ? 'text-green-500' : 'text-gray-400'}`}>
          <FiEye className="mr-1" />
          {params.value}
        </div>
      )
    },
    { 
      field: 'adminShipped', 
      headerName: 'Spedito', 
      width: 90,
      renderCell: (params) => (
        <div className={`flex items-center ${params.value === 'Yes' ? 'text-green-500' : 'text-gray-400'}`}>
          <FiTruck className="mr-1" />
          {params.value}
        </div>
      )
    },
    { 
      field: 'orderCreationTime', 
      headerName: 'Creazione Ordine', 
      width: 170,
      renderCell: (params) => (
        <div className="flex items-center">
          <FiCalendar className="mr-2 text-gray-400" />
          <span>{params.value}</span>
        </div>
      )
    },
    { 
      field: 'paymentTotal', 
      headerName: 'Totale', 
      width: 100,
      renderCell: (params) => (
        <div className="font-medium text-gray-900">€{params.value?.toFixed(2)}</div>
      )
    },
    { 
      field: 'actions', 
      headerName: 'Azioni', 
      width: 220,
      renderCell: (params) => (
        <div className="flex space-x-2 mt-2">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center px-3 py-1.5 bg-[#406580] text-white rounded-md text-sm font-medium hover:bg-[#305570] transition-colors"
            onClick={() => handleEditClick(params.row.id)}
          >
            <FiEdit className="mr-1" size={14} />
            Modifica
          </motion.button>
          
          <motion.button 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center px-3 py-1.5 bg-gray-200 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-300 transition-colors"
            onClick={() => {
              const address = encodeURIComponent(params.row.shippingAddress);
              window.open(`https://www.google.com/maps/search/?api=1&query=${address}`, '_blank');
            }}
          >
            <FiMap className="mr-1" size={14} />
            Mappa
          </motion.button>
        </div>
      )
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.3, ease: "easeOut" }
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      transition: { duration: 0.2, ease: "easeIn" }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 border-t-4 border-b-4 border-[#406580] rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600 font-medium">Caricamento ordini...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.section 
      initial="hidden" 
      animate="visible" 
      variants={containerVariants} 
      className="w-full mt-1 max-w-[102rem]"
    >
      <motion.div variants={itemVariants} className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Gestione Ordini</h1>
          <p className="text-sm text-gray-600 mt-1 mb-2">Gestisci e visualizza tutti gli ordini della piattaforma</p>
        </div>
        
        <div className="flex items-center space-x-4">
          <motion.div variants={itemVariants} className="bg-white rounded-lg border border-gray-200 flex items-center px-3 py-2 shadow-sm">
            <FiSearch className="text-gray-400 mr-2" />
            <input
              type="text"
              placeholder="Cerca ordini..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border-none focus:ring-0 text-sm outline-none"
            />
          </motion.div>
          
          <motion.div variants={itemVariants}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="status-filter-label">Stato</InputLabel>
              <Select
                labelId="status-filter-label"
                value={filterStatus}
                label="Stato"
                onChange={(e) => setFilterStatus(e.target.value)}
                sx={{ borderRadius: '0.5rem' }}
              >
                <MenuItem value="all">Tutti</MenuItem>
                <MenuItem value="pending">In attesa</MenuItem>
                <MenuItem value="paid">Pagato</MenuItem>
                <MenuItem value="processing">In lavorazione</MenuItem>
                <MenuItem value="shipped">Spedito</MenuItem>
                <MenuItem value="delivered">Consegnato</MenuItem>
                <MenuItem value="cancelled">Annullato</MenuItem>
              </Select>
            </FormControl>
          </motion.div>
        </div>
      </motion.div>

      {copyFeedback && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className="mb-4 p-2 bg-green-100 text-green-800 rounded-md text-sm flex items-center"
        >
          <FiPackage className="mr-2" /> {copyFeedback}
        </motion.div>
      )}

      <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <Box sx={{ height: 750, width: '100%' }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 10,
                },
              },
              sorting: {
                sortModel: [{ field: 'rawOrderCreationTime', sort: 'desc' }],
              },
            }}
            pageSizeOptions={[5, 10, 25, 50]}
            checkboxSelection
            disableRowSelectionOnClick
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f9fafb',
                borderBottom: '1px solid #e5e7eb',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #f3f4f6',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#f9fafb',
              },
            }}
          />
        </Box>
      </motion.div>

      <AnimatePresence>
        {selectedOrder && (
          <Modal 
            open={Boolean(selectedOrder)} 
            onClose={handleCloseModal}
            closeAfterTransition
            BackdropComponent={({ children }) => (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={handleCloseModal}
                className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
              >
                {children}
              </motion.div>
            )}
          >
            <div>
              <motion.div
                variants={modalVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="absolute top-[125px] left-1/2 -ml-[300px] bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto z-[50]"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex justify-between items-center mb-6 border-b border-gray-100 pb-4">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-[#406580] rounded-full flex items-center justify-center text-white font-medium mr-3">
                      <FiPackage size={20} />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-800">Modifica Ordine</h2>
                      <p className="text-sm text-gray-500">#{selectedOrder.id}</p>
                    </div>
                  </div>
                  <button 
                    onClick={handleCloseModal}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="space-y-6 mb-6">
                  <FormControl fullWidth>
                    <InputLabel id="status-select-label">Stato Ordine</InputLabel>
                    <Select
                      labelId="status-select-label"
                      value={selectedOrder.status}
                      label="Stato Ordine"
                      onChange={(e) => setSelectedOrder({ ...selectedOrder, status: e.target.value })}
                    >
                      <MenuItem value="pending">In attesa</MenuItem>
                      <MenuItem value="paid">Pagato</MenuItem>
                      <MenuItem value="processing">In lavorazione</MenuItem>
                      <MenuItem value="shipped">Spedito</MenuItem>
                      <MenuItem value="delivered">Consegnato</MenuItem>
                      <MenuItem value="cancelled">Annullato</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    label="Indirizzo di Spedizione"
                    fullWidth
                    multiline
                    rows={2}
                    value={selectedOrder.shippingAddress || ''}
                    onChange={(e) => setSelectedOrder({ ...selectedOrder, shippingAddress: e.target.value })}
                    className="bg-gray-50"
                  />
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormControl fullWidth>
                      <InputLabel id="viewed-select-label">Visualizzato</InputLabel>
                      <Select
                        labelId="viewed-select-label"
                        value={selectedOrder.adminViewed}
                        label="Visualizzato"
                        onChange={(e) => setSelectedOrder({ ...selectedOrder, adminViewed: e.target.value })}
                      >
                        <MenuItem value="Yes">Si</MenuItem>
                        <MenuItem value="No">No</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <FormControl fullWidth>
                      <InputLabel id="shipped-select-label">Spedito</InputLabel>
                      <Select
                        labelId="shipped-select-label"
                        value={selectedOrder.adminShipped}
                        label="Spedito"
                        onChange={(e) => setSelectedOrder({ ...selectedOrder, adminShipped: e.target.value })}
                      >
                        <MenuItem value="Yes">Si</MenuItem>
                        <MenuItem value="No">No</MenuItem>
                      </Select>
                    </FormControl>
                  </div>

                  {/* Payment Information */}
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                    <h3 className="font-medium text-gray-800 mb-3">Informazioni di Pagamento</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">ID Pagamento</p>
                        <div className="flex items-center mt-1">
                          <a 
                            href={`https://dashboard.stripe.com/payments/${selectedOrder.stripePaymentId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500 hover:text-blue-700 hover:underline flex items-center"
                          >
                            <FiLink className="mr-1" />
                            <span className="font-mono">{selectedOrder.stripePaymentId}</span>
                          </a>
                        </div>
                      </div>
                      <div>
                        <p className="text-gray-500">Totale</p>
                        <p className="font-medium mt-1">€{selectedOrder.paymentTotal?.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Data Ordine</p>
                        <p className="mt-1">{selectedOrder.orderCreationTime}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Data Pagamento</p>
                        <p className="mt-1">{selectedOrder.paymentExecutionTime}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-100">
                  <motion.button 
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={handleCloseModal}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                  >
                    Annulla
                  </motion.button>
                  <motion.button 
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={handleSaveOrder}
                    disabled={saveLoading}
                    className="px-4 py-2 bg-[#406580] text-white rounded-lg font-medium hover:bg-[#305570] transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex items-center"
                  >
                    {saveLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Salvataggio...
                      </>
                    ) : (
                      <>
                        <FiSave className="mr-2" />
                        Salva Modifiche
                      </>
                    )}
                  </motion.button>
                </div>
              </motion.div>
            </div>
          </Modal>
        )}
      </AnimatePresence>
    </motion.section>
  );
}